import { history } from 'umi';
import {
  isCompleteHttpUrl,
  getDatabrainSystemCode,
  getDatabrainSystemCodeFromPath,
  getUrlObject,
  isDatabrainUrl,
} from '@home/utils/urlConfig';
import { EntityType } from '@home/enums/urlCollection';
import { DBSystemCode } from '@home/_config/config';

// import pcGameDefaultIcon from '@/pages/dashboard/assets/svg/pc-game-default.svg';
// import mobileGameDefaultIcon from '@/pages/dashboard/assets/svg/mobile-game-default.svg';
// import consoleGameDefaultIcon from '@/pages/dashboard/assets/svg/console-game-default.svg';
import gameDefaultIcon from '@home/assets/img/game-avatar.jpg';
import intelligenceCompanyDefaultIcon from '@/pages/intelligence/assets/imgs/common/company-default.svg';

import type { TDBSystemCode } from '@home/types/aiSearch';
import type {
  INewFeatureItem,
  IUrlCollectionData,
  IUrlCollectionEntityType,
  IUrlCollectionItem,
} from '@home/types/urlCollection';

const BASE_PATH_REGEXP = /^\/(?:v2|rebuildv2)(?=\/)/i;
const DATABRAIN_BASE_PATH_WITHOUT_SLASH_REGEXP = /^(?:v2|rebuildv2)(?=\/|$)/i;

const SYSTEM_CODE_MAP: Record<string, TDBSystemCode> = {
  [DBSystemCode.Home.toLowerCase()]: DBSystemCode.Home,
  [DBSystemCode.Intelligence.toLowerCase()]: DBSystemCode.Intelligence,
  [DBSystemCode.Dashboard.toLowerCase()]: DBSystemCode.Dashboard,
  [DBSystemCode.Opinions.toLowerCase()]: DBSystemCode.Opinions,
  [DBSystemCode.Marketing.toLowerCase()]: DBSystemCode.Marketing,
  [DBSystemCode.Globalpedia.toLowerCase()]: DBSystemCode.Globalpedia,
  [DBSystemCode.StudioHub.toLowerCase()]: DBSystemCode.StudioHub,
  [DBSystemCode.Mgmt.toLowerCase()]: DBSystemCode.Mgmt,
  [DBSystemCode.Benchmark.toLowerCase()]: DBSystemCode.Benchmark,
  [DBSystemCode.UserCenter.toLowerCase()]: DBSystemCode.UserCenter,
  [DBSystemCode.Datalab.toLowerCase()]: DBSystemCode.Datalab,
  opinion: DBSystemCode.Opinions,
  opinions: DBSystemCode.Opinions,
  gpop: DBSystemCode.Globalpedia,
  'g-pop': DBSystemCode.Globalpedia,
  globalpedia: DBSystemCode.Globalpedia,
  strategic: DBSystemCode.Mgmt,
  mgmt: DBSystemCode.Mgmt,
  studio: DBSystemCode.StudioHub,
  studiohub: DBSystemCode.StudioHub,
  'user-center': DBSystemCode.UserCenter,
  usercenter: DBSystemCode.UserCenter,
};

const ENTITY_TYPE_MAP: Record<string, IUrlCollectionEntityType> = {
  [EntityType.Mobile]: EntityType.Mobile,
  [EntityType.PC]: EntityType.PC,
  [EntityType.Console]: EntityType.Console,
  [EntityType.Combine]: EntityType.Combine,
  [EntityType.Company]: EntityType.Company,
};

const isObject = (value: any): boolean => {
  return Object.prototype.toString.call(value) === '[object Object]';
};

const normalizeUrlCollectionTargetUrl = (url = ''): string => {
  const trimmedUrl = url.trim();

  if (!trimmedUrl || trimmedUrl.startsWith('/') || trimmedUrl.startsWith('//') || trimmedUrl.includes('://')) {
    return trimmedUrl;
  }

  if (DATABRAIN_BASE_PATH_WITHOUT_SLASH_REGEXP.test(trimmedUrl)) {
    return `/${trimmedUrl}`;
  }

  return trimmedUrl;
};

export const normalizeUrlCollectionSystemCode = (system = ''): TDBSystemCode | '' => {
  const normalizedSource = normalizeUrlCollectionTargetUrl(system);

  if (!normalizedSource) {
    return '';
  }

  let inferredSystem = normalizedSource;

  if (normalizedSource.includes('://')) {
    inferredSystem = getDatabrainSystemCode(normalizedSource);
  } else if (normalizedSource.startsWith('/')) {
    inferredSystem = getDatabrainSystemCodeFromPath(normalizedSource);
  }
  const normalizedSystem = inferredSystem.trim().toLowerCase();

  if (!normalizedSystem) {
    return '';
  }

  return SYSTEM_CODE_MAP[normalizedSystem] || '';
};

export const normalizeUrlCollectionEntityType = (entityType: unknown): IUrlCollectionEntityType => {
  const rawEntityType = Array.isArray(entityType) ? entityType[0] : entityType;
  const normalizedEntityType = String(rawEntityType || '')
    .trim()
    .toLowerCase();

  return ENTITY_TYPE_MAP[normalizedEntityType] || '';
};

export const getUrlCollectionPrimaryEntityType = (
  entityType?: IUrlCollectionEntityType | EntityType[] | '',
): IUrlCollectionEntityType => {
  return normalizeUrlCollectionEntityType(entityType || '');
};

export const parseUrlCollectionData = (data = ''): IUrlCollectionData => {
  if (!data) {
    return {};
  }

  try {
    const parsedData = JSON.parse(data);

    if (!isObject(parsedData)) {
      return {};
    }

    return parsedData as IUrlCollectionData;
  } catch (error) {
    return {};
  }
};

export const stringifyUrlCollectionData = (data: IUrlCollectionData = {}): string => {
  const nextData = { ...data };

  Object.keys(nextData).forEach((key) => {
    if (nextData[key] === undefined || nextData[key] === null || nextData[key] === '') {
      delete nextData[key];
    }
  });

  if (!Object.keys(nextData).length) {
    return '';
  }

  return JSON.stringify(nextData);
};

export const mergeUrlCollectionData = (data = '', patch: Partial<IUrlCollectionData>): string => {
  return stringifyUrlCollectionData({
    ...parseUrlCollectionData(data),
    ...patch,
  });
};

export const normalizeUrlCollectionItem = (item: any): IUrlCollectionItem => {
  const normalizedSystem = normalizeUrlCollectionSystemCode(item?.url || '');
  const fallbackSystem = normalizeUrlCollectionSystemCode(item?.system || '');

  return {
    id: String(item?.id || ''),
    system: normalizedSystem || fallbackSystem || item?.system || DBSystemCode.Home,
    url: item?.url || '',
    name: item?.name || '',
    cover: item?.cover || '',
    entity_type: getUrlCollectionPrimaryEntityType(item?.entity_type || ''),
    entity_id: item?.entity_id || '',
    entity_name: item?.entity_name || '',
    data: typeof item?.data === 'string' ? item.data : stringifyUrlCollectionData(item?.data || {}),
    order: Number(item?.order || 0),
    page_id: item?.page_id || '',
    is_default: !!item?.is_default,
  };
};

export const normalizeNewFeatureItem = (item: any): INewFeatureItem => {
  const redirectPath = normalizeUrlCollectionTargetUrl(item?.redirect_path || '');
  const normalizedSystem = normalizeUrlCollectionSystemCode(redirectPath);
  const fallbackSystem = normalizeUrlCollectionSystemCode(item?.system || '');

  return {
    id: Number(item?.id || 0),
    system: normalizedSystem || fallbackSystem || item?.system || DBSystemCode.Home,
    page_name_cn: item?.page_name_cn || '',
    page_name_en: item?.page_name_en || '',
    description_cn: item?.description_cn || '',
    description_en: item?.description_en || '',
    display_image: item?.display_image || '',
    redirect_path: redirectPath,
    page_id: item?.page_id || '',
    online_time: item?.online_time || '',
    is_hot: !!item?.is_hot,
  };
};

export const sortUrlCollectionItems = (items: IUrlCollectionItem[]): IUrlCollectionItem[] => {
  return [...items].sort((prevItem, nextItem) => {
    return prevItem.order - nextItem.order;
  });
};

export const reindexUrlCollectionItems = (items: IUrlCollectionItem[]): IUrlCollectionItem[] => {
  return items.map((item, index) => {
    return {
      ...item,
      order: index + 1,
    };
  });
};

export const sanitizeUrlCollectionItemsForSave = (items: IUrlCollectionItem[]): IUrlCollectionItem[] => {
  return reindexUrlCollectionItems(items).map((item) => {
    return {
      ...item,
      id: item.id.startsWith('temp-') ? '' : item.id,
      system:
        normalizeUrlCollectionSystemCode(item.url || '') ||
        normalizeUrlCollectionSystemCode(item.system) ||
        item.system,
      entity_name: undefined,
      data: stringifyUrlCollectionData(parseUrlCollectionData(item.data)),
    };
  });
};

export const getUrlCollectionItemKey = (item: IUrlCollectionItem): string => {
  return item.id || `${item.system}-${item.url}-${item.order}`;
};

export const getShortcutDisplayName = (item: IUrlCollectionItem): string => {
  return item.name;
};

const getEntityDefaultImage = (entityType: IUrlCollectionEntityType): string => {
  switch (entityType) {
    case EntityType.PC:
      // return pcGameDefaultIcon;
      return gameDefaultIcon;
    case EntityType.Console:
    case EntityType.Combine:
      // return consoleGameDefaultIcon;
      return gameDefaultIcon;
    case EntityType.Mobile:
      // return mobileGameDefaultIcon;
      return gameDefaultIcon;
    case EntityType.Company:
      return intelligenceCompanyDefaultIcon;
    default:
      return '';
  }
};

interface IGetUrlCollectionDisplayImageOptions {
  entityType?: IUrlCollectionEntityType | EntityType[] | '';
  cover?: string;
  systemIcon?: string;
}

export const getUrlCollectionDisplayImage = (options: IGetUrlCollectionDisplayImageOptions): string => {
  const entityType = getUrlCollectionPrimaryEntityType(options?.entityType);
  const cover = options?.cover || '';

  if (!entityType) {
    return options?.systemIcon || '';
  }

  return cover || getEntityDefaultImage(entityType);
};

export const getShortcutDisplayCover = (item: IUrlCollectionItem, systemIcon = ''): string => {
  const data = parseUrlCollectionData(item?.data);

  return getUrlCollectionDisplayImage({
    entityType: item?.entity_type,
    cover: data?.entity_cover || item?.cover || '',
    systemIcon,
  });
};

export const getShortcutPlatformTypes = (item: IUrlCollectionItem): EntityType[] => {
  const entityType = getUrlCollectionPrimaryEntityType(item.entity_type);

  return entityType && entityType !== EntityType.Company ? [entityType] : [];
};

export const getUrlCollectionPagePath = (url = ''): string => {
  const normalizedUrl = normalizeUrlCollectionTargetUrl(url);

  if (!normalizedUrl) {
    return '';
  }

  const urlObj = getUrlObject(normalizedUrl);

  if (!urlObj) {
    return normalizedUrl;
  }

  const pathname = urlObj.pathname.replace(BASE_PATH_REGEXP, '') || '/';
  return `${pathname}${urlObj.search}`;
};

export const getUrlCollectionRedirectPath = (url = ''): string => {
  const pagePath = getUrlCollectionPagePath(url);

  if (!pagePath) {
    return '';
  }

  const normalizedUrl = normalizeUrlCollectionTargetUrl(url);
  const urlObj = getUrlObject(normalizedUrl);

  if (!urlObj) {
    return pagePath;
  }

  return `${pagePath}${urlObj.hash}`;
};

export const isSafeUrlCollectionTargetUrl = (url = ''): boolean => {
  const trimmedUrl = normalizeUrlCollectionTargetUrl(url);

  if (!trimmedUrl || trimmedUrl.startsWith('//')) {
    return false;
  }

  if (trimmedUrl.startsWith('/')) {
    return true;
  }

  return isCompleteHttpUrl(trimmedUrl);
};

export const isUrlCollectionInternalUrl = (url = ''): boolean => {
  const trimmedUrl = normalizeUrlCollectionTargetUrl(url);

  return isSafeUrlCollectionTargetUrl(trimmedUrl) && (trimmedUrl.startsWith('/') || isDatabrainUrl(trimmedUrl));
};

export const getUrlCollectionComparableUrl = (url = ''): string => {
  const trimmedUrl = normalizeUrlCollectionTargetUrl(url);

  if (!trimmedUrl) {
    return '';
  }

  const urlObj = getUrlObject(trimmedUrl);

  if (!urlObj) {
    return trimmedUrl;
  }

  const normalizedSearchParams = Array.from(urlObj.searchParams.entries()).sort(
    ([prevKey, prevValue], [nextKey, nextValue]) => {
      if (prevKey === nextKey) {
        return prevValue.localeCompare(nextValue);
      }

      return prevKey.localeCompare(nextKey);
    },
  );
  const normalizedSearchValue = normalizedSearchParams
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  const normalizedSearch = normalizedSearchValue ? `?${normalizedSearchValue}` : '';
  const normalizedHash = urlObj.hash || '';

  if (isUrlCollectionInternalUrl(trimmedUrl)) {
    const pathname = urlObj.pathname.replace(BASE_PATH_REGEXP, '') || '/';

    return `${pathname}${normalizedSearch}${normalizedHash}`;
  }

  return `${urlObj.origin}${urlObj.pathname}${normalizedSearch}${normalizedHash}`;
};

export const openUrlCollectionExternalUrl = (url = ''): void => {
  const trimmedUrl = url.trim();

  if (!isSafeUrlCollectionTargetUrl(trimmedUrl) || !isCompleteHttpUrl(trimmedUrl)) {
    return;
  }

  window.open(trimmedUrl, '_blank', 'noopener,noreferrer');
};

export const navigateUrlCollectionUrl = (url = ''): boolean => {
  const trimmedUrl = normalizeUrlCollectionTargetUrl(url);

  if (!isUrlCollectionInternalUrl(trimmedUrl)) {
    return false;
  }

  const redirectPath = getUrlCollectionRedirectPath(trimmedUrl);

  if (!redirectPath) {
    return false;
  }

  history.push(redirectPath);
  return true;
};
